import type { ChangeEvent, FocusEvent } from 'react';
import { type RegisterOptions, useFormContext } from 'react-hook-form';

interface CallerHandlers<TElement> {
  onChange?: ((event: ChangeEvent<TElement>) => void) | undefined;
  onBlur?: ((event: FocusEvent<TElement>) => void) | undefined;
}

/**
 * `register(name)` plus the caller's own onChange/onBlur, composed in that order.
 *
 * Spreading `...props` after `register(name)` overwrites RHF's handlers, which silently
 * disables validation for any caller that passes one — the bug that was fixed in
 * `text-field.tsx` and existed identically in every other field. Routing all of them
 * through here means it can only be fixed once.
 */
export function useRegistration<TElement extends HTMLElement>(
  name: string,
  handlers: CallerHandlers<TElement> = {},
  options?: RegisterOptions,
) {
  const { register } = useFormContext();
  const registration = register(name, options);
  const { onChange, onBlur } = handlers;

  return {
    ...registration,
    onBlur: (event: FocusEvent<TElement>) => {
      void registration.onBlur(event);
      onBlur?.(event);
    },
    onChange: (event: ChangeEvent<TElement>) => {
      void registration.onChange(event);
      onChange?.(event);
    },
  };
}
